// Lot C (CDC du 09/10 au soir, § 5 ; réponse de Marc à Q3 le 11/10 : « ce qui est trop gros, c'est
// surtout l'écriture ») — les étapes d'un permis SITADEL et les couleurs de la carte des projets,
// à la charte.
//
// C8 — UNE liste de libellés d'étape, lue par les tuiles, le sélecteur « Étape », la table, la
// bulle, l'éventail et le détail. L'état 5 s'appelait « En chantier » dans les tuiles et
// « Commencé » dans la table et la bulle, malgré le commentaire qui exigeait l'égalité : il n'y a
// plus qu'un endroit où l'écrire. L'export Excel écrit les mêmes mots (`ETAPES` de
// backend/app/observatoire/sitadel_export.py) — seuls les libellés changent, jamais les codes.
//
// C5 — natures et étapes tirées des jetons de la charte (obs.css) : plus de violet, d'ambre, de
// sarcelle, de bleu ni de vert hors jetons, et plus de rouge (identité : « jamais de rouge »).
import type { CSSProperties } from 'react'

/** Les jetons de la charte en hexadécimal : MapLibre ne lit pas les variables CSS. */
export const JETONS = {
  marine: '#0D1F3C',      // --c-brand
  log: '#E84B00',         // --c-log (thématique Logement)
  logMarine: '#9B3C15',   // color-mix(in srgb, var(--c-log) 65 %, var(--c-brand)) — 6,9:1 sur blanc
  muted: '#686E7C',       // --c-muted
} as const

/** Les codes SDES de l'état d'avancement, et leur seul libellé à l'écran (et dans l'export). */
export const LIBELLES_ETAPE: Record<string, string> = {
  '1': 'Non commencé',
  '2': 'En attente de démarrage',
  '3': 'En sursis',
  '4': 'Annulé',
  '5': 'En chantier',
  '6': 'Terminé',
}

export const libelleEtape = (code?: string | null): string => (code ? LIBELLES_ETAPE[code] ?? code : '–')

export type StatusKey = 'autorise' | 'commence' | 'termine' | 'annule'

// Les quatre étapes suivies (tuiles, sélecteur, export). Les CLÉS sont celles que sert le back
// (`/sitadel-contexte`, `statuts.autorise`…) ; les libellés sont purement d'affichage.
// C7 (08/08) : l'état 2 ne s'appelle plus « Autorisé » (le mot désignait aussi un événement, la
// délivrance) ni « Autorisé, non commencé » (collision avec l'état 1, « Non commencé ») : la
// séquence se lit seule — en attente de démarrage → en chantier → terminé.
// Couleurs : la thématique Logement s'assombrit vers la marine à mesure que le projet avance ;
// l'annulé est gris.
export const STATUS_META: Record<StatusKey, { codes: string[]; label: string; color: string }> = {
  autorise: { codes: ['2'], label: LIBELLES_ETAPE['2'], color: JETONS.log },
  commence: { codes: ['5'], label: LIBELLES_ETAPE['5'], color: JETONS.logMarine },
  termine: { codes: ['6'], label: LIBELLES_ETAPE['6'], color: JETONS.marine },
  annule: { codes: ['4'], label: LIBELLES_ETAPE['4'], color: JETONS.muted },
}
export const ETAPES_SUIVIES = Object.keys(STATUS_META) as StatusKey[]

// Les puces d'étape (table, bulle, détail) : 10 px comme avant, couleurs des jetons. Texte sur
// teinte claire ≥ 4,5:1, sauf « En chantier », pleine (blanc sur le mélange : 6,9:1).
const PUCES: Record<string, { color: string; background: string }> = {
  '2': { color: JETONS.logMarine, background: '#FDF3EE' },
  '5': { color: '#FFFFFF', background: JETONS.logMarine },
  '6': { color: JETONS.marine, background: '#EEF2F7' },
}
const PUCE_NEUTRE = { color: JETONS.muted, background: '#F1F2F4' }

export function stylePuceEtape(code?: string | null): CSSProperties {
  return { fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 600, whiteSpace: 'nowrap',
           ...(code ? PUCES[code] ?? PUCE_NEUTRE : PUCE_NEUTRE) }
}

// ── C1 : la ligne de chiffres qui remplace les quatre tuiles de résumé ─────────────────────────
/** Les champs de la synthèse SERVEUR (`/sitadel-contexte`) que lit la ligne — aucun recalcul. */
export interface SyntheseResume {
  periode: { min: number; max: number } | null
  permis: number
  logements: number
  collectifs: number
  social_projete: number
  stock_rpls: { total_units: number } | null
  rpls_millesime: string
}
export interface ChiffreResume {
  cle: 'permis' | 'logements' | 'collectifs' | 'social' | 'rpls' | 'annee'
  valeur: string
  texte: string
  /** le terme du glossaire (`DefTerm`) qui porte le texte */
  terme?: string
  aide?: string
}

const s = (n: number) => (n > 1 ? 's' : '')
const social = (n: number) => (n > 1 ? 'sociaux' : 'social')

/**
 * « 1 823 permis · 1 006 logements autorisés depuis 2013 · 64 % en collectif · +210 logements
 * sociaux projetés · 12 345 logements sociaux existants (RPLS au 2024) · 36 permis en 2026 ».
 * Sans synthèse serveur : rien (le N/D se dit à l'écran) — un chiffre recalculé dans le
 * navigateur sortirait du système de garanties (spec L-O). `derniere` : l'année la plus récente
 * des permis créant un logement, et son nombre de permis (le même périmètre que la synthèse).
 */
export function chiffresResume(sy: SyntheseResume | null | undefined, derniere: { annee: number; permis: number } | null,
                               fmt: (n: number) => string): ChiffreResume[] {
  if (!sy) return []
  const periode = sy.periode ? `${sy.periode.min}-${sy.periode.max}` : null
  const out: ChiffreResume[] = [
    { cle: 'permis', valeur: fmt(sy.permis), texte: 'permis',
      aide: `Permis créant au moins un logement${periode ? `, enregistrés de ${sy.periode!.min} à ${sy.periode!.max}` : ''}` },
    { cle: 'logements', valeur: fmt(sy.logements), texte: `logement${s(sy.logements)} autorisé${s(sy.logements)}${sy.periode ? ` depuis ${sy.periode.min}` : ''}`,
      terme: 'logements-autorises', aide: periode ? `Prévus par les permis ${periode}, toutes années cumulées` : undefined },
  ]
  if (sy.logements > 0) {
    out.push({ cle: 'collectifs', valeur: `${Math.round((sy.collectifs / sy.logements) * 100)} %`, texte: 'en collectif',
               terme: 'logement-collectif', aide: `${fmt(sy.collectifs)} logements collectifs` })
  }
  const stock = sy.stock_rpls?.total_units ?? 0
  out.push({ cle: 'social', valeur: `+${fmt(sy.social_projete)}`, texte: `logement${s(sy.social_projete)} ${social(sy.social_projete)} projeté${s(sy.social_projete)}`,
             aide: stock > 0 && periode
               ? `Permis ${periode} : jusqu'à +${fmt((sy.social_projete / stock) * 100)} % du parc social (RPLS au ${sy.rpls_millesime}) si toutes les autorisations aboutissent`
               : undefined })
  if (sy.stock_rpls) {
    out.push({ cle: 'rpls', valeur: fmt(stock), texte: `logement${s(stock)} ${social(stock)} existant${s(stock)} (RPLS au ${sy.rpls_millesime})`,
               terme: 'rpl-rpls' })
  }
  if (derniere) out.push({ cle: 'annee', valeur: fmt(derniere.permis), texte: `permis en ${derniere.annee}` })
  return out
}
